import React, { useState } from "react";
import { option } from "fp-ts/lib";
import * as FirstPartyFetchResponse from "../../../../../domain/models/FirstPartyFetchResponse";
import * as fetchWrapper from "../../../wrappers/fetch";
import { introducerPortalBasePath } from "../getIntroducerSlugFromUrl";
import { getApiUrl } from "../../../wrappers/apiUrl";
import { Background } from "../../../components/BuildingBlocks/Background";
import { Padding } from "../../../components/BuildingBlocks/Padding";
import { SpacingColumn } from "../../../components/BuildingBlocks/SpacingColumn";
import { FontRobotoSlab } from "../../../components/BuildingBlocks/FontRobotoSlab";
import { FontQuicksand } from "../../../components/BuildingBlocks/FontQuicksand";
import { FontSize } from "../../../components/BuildingBlocks/FontSize";
import { TextColor } from "../../../components/BuildingBlocks/TextColor";
import { TextCenter } from "../../../components/TextCenter/TextCenter";
import { CursorPointer } from "../../../components/CursorPointer/CursorPointer";
import { WeightMedium } from "../../../components/WeightMedium/WeightMedium";
import { WeightBold } from "../../../components/WeightBold/WeightBold";
import { FrontColors } from "../../../models/FrontColors";
import { FrontFontSizes } from "../../../models/FrontFontSizes";
import { FrontSpacing } from "../../../models/FrontSpacing";
import { FPInput } from "../../../components/FrontProbate/Complex/FPInput/FPInput";
import { FPSimpleLogo } from "../../../components/FrontProbate/Simple/FPSimpleLogo/FPSimpleLogo";

type TSubmitStatus = "idle" | "submitting" | "sent" | "error";

export const IntroducerPortalLoginPage = (): JSX.Element => {
    const [email, setEmail] = useState<string>("");
    const [status, setStatus] = useState<TSubmitStatus>("idle");

    const submit = (): void => {
        if (!email.trim() || status === "submitting") {
            return;
        }
        setStatus("submitting");
        fetchWrapper.noContent({
            requestParams: {
                url: `${getApiUrl()}/v1/public/send-magic-link`,
                method: "POST",
                body: option.some(JSON.stringify({
                    email: email.trim().toLowerCase(),
                    redirect_url: `${window.location.origin}${introducerPortalBasePath()}/dashboard`,
                })),
            },
        })().then((response) => {
            setStatus(response.tag === FirstPartyFetchResponse.constants.STATUS_204 ? "sent" : "error");
        });
    };

    return (
        <Background color={FrontColors.PROBATE_0} minHeight="100vh">
            <Padding type="custom" spacing={`${FrontSpacing.LARGE_3} ${FrontSpacing.MEDIUM_1}`}>
                <div style={{ maxWidth: "560px", width: "100%", marginLeft: "auto", marginRight: "auto" }}>
                    <SpacingColumn spacing={FrontSpacing.MEDIUM_2}>
                            <TextCenter>
                                <FPSimpleLogo width="60px" />
                            </TextCenter>

                            <TextCenter>
                                <FontRobotoSlab>
                                    <FontSize size={FrontFontSizes.HUGE} lineHeight="1.1">
                                        <TextColor color={FrontColors.NEUTRAL_18}>
                                            <WeightMedium>Your introducer portal.</WeightMedium>
                                        </TextColor>
                                    </FontSize>
                                </FontRobotoSlab>
                            </TextCenter>

                            <TextCenter>
                                <FontQuicksand>
                                    <FontSize size={FrontFontSizes.MED} lineHeight="1.45">
                                        <TextColor color={FrontColors.NEUTRAL_18}>
                                            Track <WeightBold>every case</WeightBold> we&apos;re running for you — pipeline, marketing, conveyancing — in one place. Enter your email and we&apos;ll send you a one-time <WeightBold>sign-in link</WeightBold>.
                                        </TextColor>
                                    </FontSize>
                                </FontQuicksand>
                            </TextCenter>

                            <Padding type="top" spacing={FrontSpacing.MEDIUM_1}>
                                <SpacingColumn spacing={FrontSpacing.SMALL_3}>
                                    <FontQuicksand>
                                        <FontSize size={FrontFontSizes.X_SMALL}>
                                            <TextColor color={FrontColors.NEUTRAL_18}>
                                                <WeightMedium>Email address</WeightMedium>
                                            </TextColor>
                                        </FontSize>
                                    </FontQuicksand>

                                    <FPInput
                                        coloursInverted={true}
                                        value={email}
                                        placeholder="you@yourfirm.co.uk"
                                        autoFocus={true}
                                        onChange={setEmail}
                                        onEnter={submit}
                                    />

                                    <CursorPointer onClick={submit}>
                                        <Background
                                            color={FrontColors.NEUTRAL_18}
                                            borderRadius="5px"
                                        >
                                            <Padding type="custom" spacing={`${FrontSpacing.SMALL_3} ${FrontSpacing.MEDIUM_1}`}>
                                                <TextCenter>
                                                    <FontRobotoSlab>
                                                        <FontSize size={FrontFontSizes.SMALL}>
                                                            <TextColor color={FrontColors.PROBATE_0}>
                                                                <WeightBold>
                                                                    {status === "submitting" ? "Sending…" : "Send sign-in link"}
                                                                </WeightBold>
                                                            </TextColor>
                                                        </FontSize>
                                                    </FontRobotoSlab>
                                                </TextCenter>
                                            </Padding>
                                        </Background>
                                    </CursorPointer>

                                    {status === "sent" && (
                                        <Padding type="top" spacing={FrontSpacing.SMALL_3}>
                                            <Background
                                                color={FrontColors.NEUTRAL_18}
                                                borderRadius="5px"
                                            >
                                                <Padding type="custom" spacing={`${FrontSpacing.SMALL_3} ${FrontSpacing.MEDIUM_1}`}>
                                                    <FontQuicksand>
                                                        <FontSize size={FrontFontSizes.MINOR} lineHeight="1.45">
                                                            <TextColor color={FrontColors.NEUTRAL_INK}>
                                                                <WeightBold>Check your inbox.</WeightBold> If we recognise that email, your sign-in link is on its way. It can take a minute or two — and may land in spam.
                                                            </TextColor>
                                                        </FontSize>
                                                    </FontQuicksand>
                                                </Padding>
                                            </Background>
                                        </Padding>
                                    )}
                                    {status === "error" && (
                                        <Padding type="top" spacing={FrontSpacing.SMALL_3}>
                                            <Background
                                                color={FrontColors.ERROR_FIREBRICK_22}
                                                borderRadius="5px"
                                            >
                                                <Padding type="custom" spacing={`${FrontSpacing.SMALL_3} ${FrontSpacing.MEDIUM_1}`}>
                                                    <FontQuicksand>
                                                        <FontSize size={FrontFontSizes.MINOR} lineHeight="1.45">
                                                            <TextColor color={FrontColors.ERROR_FIREBRICK_4}>
                                                                Couldn&apos;t send your sign-in link. Try again, or contact your Sail Legal contact if it keeps happening.
                                                            </TextColor>
                                                        </FontSize>
                                                    </FontQuicksand>
                                                </Padding>
                                            </Background>
                                        </Padding>
                                    )}
                                </SpacingColumn>
                            </Padding>
                    </SpacingColumn>
                </div>
            </Padding>
        </Background>
    );
};

export default IntroducerPortalLoginPage;
